import type { ElectricalProjectInspectionKind } from '@connected/elektra-sdk/validators/electrical-project';
import type { FormApi, ReactFormApi } from '@tanstack/react-form';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import {
    MultipleOptionField,
    MultipleOptionItem,
    SingleCheckOptionItem,
    SingleHorizontalOptionItem,
    SingleOptionField,
    SingleOptionItem,
} from '@connected/form-fields/components/option';
import { Input } from '@connected/ui/components/input';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import type { InspectionPlanFormValues, InspectionPlanReportFormValues, InspectionPlanReportOption } from '@/routes/(auth)/projects/$projectVersionId/inspection-plans';

import { ElseField } from '@/components/else-field';
import { queryClient } from '@/lib/query-client';
import { useOffline } from '@/stores/network-status';

type InspectionPlanReportFieldsProps = {
    form: FormApi<InspectionPlanFormValues, ZodValidator> & ReactFormApi<InspectionPlanFormValues, ZodValidator>;
    reportIndex: number;
    inspectionKind: ElectricalProjectInspectionKind;
    projectVersionId: string;
    isSingleClassification?: boolean;
    additionalInstructionSelectlistItemOptions: InspectionPlanReportOption[];
    inspectionComponentSelectlistItemOptions: InspectionPlanReportOption[];
};

const sampleMethodKindOptions: { value: InspectionPlanFormValues['sampleMethodKind']; label: string }[] = [
    {
        label: 'Indien mogelijk steekproef conform NEN3140 bijlage J',
        value: 'ConformNen3140AppendixJ',
    },
    {
        label: 'Indien mogelijk steekproef conform NTA 8220 tabel 1',
        value: 'ConformNTA8220Tabel1',
    },
    {
        label: 'Zie aanvullende instructie',
        value: 'AdditionalInstruction',
    },
    {
        label: 'De steekproef beperkt zich tot de 1e steekproefcyclus',
        value: 'LimitToFirstSampleRound',
    },
    {
        label: 'De steekproef kan conform bijlage J worden uitgebreid',
        value: 'SampleCanBeExpandedBasedOnAppendixJ',
    },
    {
        label: '100%',
        value: 'Complete',
    },
    {
        label: 'Anders',
        value: 'Other',
    },
];

const inspectionReportIntervalOptions: { value: InspectionPlanFormValues['intervalDeterminationKind']; label: string }[] = [
    {
        label: 'Inspectie frequentie is door de verzekering vast gesteld op:',
        value: 'DictatedByInsuranceCompany',
    },
    {
        label: 'Bepaling op basis van Scope 10 / NTA8220',
        value: 'NTA8220',
    },
    {
        label: 'Anders',
        value: 'Other',
    },
];

export function InspectionPlanReportFields({
    form,
    reportIndex,
    inspectionKind,
    projectVersionId,
    isSingleClassification = false,
    additionalInstructionSelectlistItemOptions,
    inspectionComponentSelectlistItemOptions,
}: InspectionPlanReportFieldsProps) {
    const { t } = useTranslation('inspection-plans');
    const isUserOffline = useOffline();
    const { context } = useElectricalProject();

    // Helper function to get field names based on mode
    function getFieldName<T extends keyof InspectionPlanReportFormValues>(field: T) {
        // For combination mode, use report-based field names
        return `reports[${reportIndex}].${field}` as const;
    }

    // Use different endpoints based on mode
    const endpoint = isSingleClassification
        ? '/api/v2/electrical/projects/inspectionplans/patch'
        : (context.isCombinationInspection
                ? '/api/v2/electrical/projects/inspectionplans/reports/patch'
                : '/api/v2/electrical/projects/inspectionplans/patch');

    const patchBody = isSingleClassification
        ? { ElectricalProjectVersionId: projectVersionId }
        : (context.isCombinationInspection
                ? { ElectricalProjectVersionId: projectVersionId, InspectionKind: inspectionKind }
                : { ElectricalProjectVersionId: projectVersionId });

    const { patchField } = usePatcher(endpoint, {
        onSettled: () => queryClient.invalidateQueries(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: projectVersionId })),
    }, { body: patchBody });

    const patchSelectlistItems = usePatcher('/api/v2/electrical/projects/selectlists/items/patch', {
        onSettled: () => {
            // Invalidate selectlist items queries to refresh the options
            queryClient.invalidateQueries(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({ electricalProjectVersionId: projectVersionId, listKind: 'AdditionalInstruction' }));
            queryClient.invalidateQueries(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({ electricalProjectVersionId: projectVersionId, listKind: 'InspectionComponent' }));
            // Also invalidate inspection plan query to refresh form values
            queryClient.invalidateQueries(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: projectVersionId }));
        },
        scope: {
            id: projectVersionId,
        },
    }, { body: { ElectricalProjectVersionId: projectVersionId } as any });

    // Helper function to handle selectlist item changes
    function handleSelectlistItemChange(
        listKind: 'AdditionalInstruction' | 'InspectionComponent',
        previousValue: string[] | null | undefined,
        newValue: string[] | null | undefined,
    ) {
        const previous = previousValue ?? [];
        const current = newValue ?? [];

        // Find added items (in current but not in previous)
        const addedItems = current.filter(item => !previous.includes(item));

        // Find removed items (in previous but not in current)
        const removedItems = previous.filter(item => !current.includes(item));

        // Patch each added item
        addedItems.forEach((itemId) => {
            if (context.isCombinationInspection) {
                patchSelectlistItems.patch({
                    ListKind: listKind,
                    Items: [{
                        ElectricalProjectSelectListItemId: itemId,
                        AddInspectionKind: inspectionKind,
                    }],
                });
            }
            else {
                patchSelectlistItems.patch({
                    ListKind: listKind,
                    Items: [{
                        ElectricalProjectSelectListItemId: itemId,
                        Selected: true,
                    }],
                });
            }
        });

        // Patch each removed item
        removedItems.forEach((itemId) => {
            if (context.isCombinationInspection) {
                patchSelectlistItems.patch({
                    ListKind: listKind,
                    Items: [{
                        ElectricalProjectSelectListItemId: itemId,
                        RemoveInspectionKind: inspectionKind,
                    }],
                });
            }
            else {
                patchSelectlistItems.patch({
                    ListKind: listKind,
                    Items: [{
                        ElectricalProjectSelectListItemId: itemId,
                        Selected: false,
                    }],
                });
            }
        });
    }

    // Filter options based on inspection kind and mode
    // In combination mode, show all options so users can add new associations
    // In non-combination mode, show all options
    const filteredAdditionalInstructionOptions = useMemo(() => {
        // Always show all options - the form state tracks which are selected for this inspection kind
        return additionalInstructionSelectlistItemOptions;
    }, [additionalInstructionSelectlistItemOptions]);

    const filteredInspectionComponentOptions = useMemo(() => {
        // Always show all options - the form state tracks which are selected for this inspection kind
        return inspectionComponentSelectlistItemOptions;
    }, [inspectionComponentSelectlistItemOptions]);

    return (
        <div className="flex flex-col gap-6">
            {/* Only show inspectionPlanNumber and inspectionPlanVersion in combination mode */}
            {!isSingleClassification && (
                <div className="grid grid-cols-2 gap-4">
                    <form.Field
                        name={getFieldName('inspectionPlanNumber')}
                        validators={{
                            onChange: validators.patchElectricalProjectInspectionPlanReportCommand.inspectionPlanNumber,
                            onChangeAsync: patchField('InspectionPlanNumber'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <Label label={t('information.form.inspectionPlanNumber.label')}>
                                <Input
                                    name={field.name}
                                    value={field.state.value ?? ''}
                                    onChange={(e) => { field.handleChange(e.target.value); }}
                                    placeholder={t('information.form.inspectionPlanNumber.placeholder')}
                                    disabled={isUserOffline}
                                />
                                <FieldInfo state={field.state} />
                            </Label>
                        )}
                    </form.Field>
                    <form.Field
                        name={getFieldName('inspectionPlanVersion')}
                        validators={{
                            onChange: validators.patchElectricalProjectInspectionPlanReportCommand.inspectionPlanVersion,
                            onChangeAsync: patchField('InspectionPlanVersion'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {field => (
                            <Label label={t('information.form.inspectionPlanVersion.label')}>
                                <Input
                                    name={field.name}
                                    value={field.state.value ?? ''}
                                    onChange={(e) => { field.handleChange(e.target.value); }}
                                    placeholder={t('information.form.inspectionPlanVersion.placeholder')}
                                    disabled={isUserOffline}
                                />
                                <FieldInfo state={field.state} />
                            </Label>
                        )}
                    </form.Field>
                </div>
            )}

            <form.Field
                name={getFieldName('sampleMethodKind')}
                validators={{
                    onChange: isSingleClassification
                        ? validators.editElectricalProjectInspectionPlanCommand.sampleMethodKind
                        : validators.patchElectricalProjectInspectionPlanReportCommand.sampleMethodKind,
                    onChangeAsync: async (value) => {
                        const response = await patchField('SampleMethodKind')(value);

                        if (value?.value !== 'Other') {
                            form.setFieldValue(getFieldName('sampleMethodOther'), '');
                        }

                        await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: projectVersionId }));

                        return response;
                    },
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {field => (
                    <Label label={t('information.form.sampleMethod.label')}>
                        <SingleOptionField
                            name={field.name}
                            handleChange={value => field.handleChange(value)}
                            state={field.state}
                            options={sampleMethodKindOptions}
                            render={SingleCheckOptionItem}
                            dropdown
                            disabled={isUserOffline}
                            closeOnClick
                            placeholder="Selecteer steekproef methode"
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            </form.Field>

            <form.Subscribe
                selector={(state) => {
                    if (isSingleClassification) {
                        return [state.values.sampleMethodKind];
                    }
                    return [state.values.reports[reportIndex]?.sampleMethodKind];
                }}
            >
                {([sampleMethodKind]) =>
                    sampleMethodKind === 'Other' && (
                        <form.Field
                            name={getFieldName('sampleMethodOther')}
                            validators={{
                                onChange: isSingleClassification
                                    ? validators.editElectricalProjectInspectionPlanCommand.sampleMethodOther
                                    : validators.patchElectricalProjectInspectionPlanReportCommand.sampleMethodOther,
                                onChangeAsync: patchField('SampleMethodOther'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <ElseField>
                                    <Label label="Anders" className="w-full">
                                        <Input
                                            name={name}
                                            placeholder="Noteer steekproef methode anders"
                                            className="w-full flex flex-row"
                                            value={state.value ?? ''}
                                            onChange={e => handleChange(e.target.value)}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                </ElseField>
                            )}
                        </form.Field>
                    )}
            </form.Subscribe>

            <form.Field
                name={getFieldName('intervalDeterminationKind')}
                validators={{
                    onChange: isSingleClassification
                        ? validators.editElectricalProjectInspectionPlanCommand.intervalDeterminationKind
                        : validators.patchElectricalProjectInspectionPlanReportCommand.intervalDeterminationKind,
                    onChangeAsync: async (value) => {
                        const response = await patchField('IntervalDeterminationKind')(value);

                        form.setFieldValue(getFieldName('nextInspectionInYears'), null);
                        form.setFieldValue(getFieldName('intervalDeterminationOther'), '');

                        await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: projectVersionId }));

                        return response;
                    },
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('information.form.inspectionInterval.label')}>
                        <SingleOptionField
                            name={name}
                            handleChange={handleChange}
                            state={state}
                            options={inspectionReportIntervalOptions}
                            render={isSingleClassification ? SingleOptionItem : SingleCheckOptionItem}
                            dropdown
                            disabled={isUserOffline}
                            closeOnClick
                            placeholder="Selecteer inspectie interval"
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </form.Field>

            <form.Subscribe
                selector={(state) => {
                    return [state.values.reports[reportIndex]?.intervalDeterminationKind];
                }}
            >
                {([intervalDeterminationKind]) =>
                    intervalDeterminationKind === 'DictatedByInsuranceCompany' && (
                        <form.Field
                            name={getFieldName('nextInspectionInYears')}
                            validators={{
                                onChange: isSingleClassification
                                    ? validators.editElectricalProjectInspectionPlanCommand.nextInspectionInYears
                                    : validators.patchElectricalProjectInspectionPlanReportCommand.nextInspectionInYears,
                                onChangeAsync: patchField('NextInspectionInYears'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label="Inspectie frequentie">
                                    <SingleOptionField
                                        name={name}
                                        placeholder="Noteer inspectie frequentie"
                                        className="w-full flex flex-row"
                                        render={SingleHorizontalOptionItem}
                                        state={state}
                                        handleChange={handleChange}
                                        options={[
                                            { label: '3 jaar', value: 3 },
                                            { label: '5 jaar', value: 5 },
                                        ]}
                                        disabled={isUserOffline}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>
                    )}
            </form.Subscribe>

            <form.Subscribe
                selector={(state) => {
                    return [state.values.reports[reportIndex]?.intervalDeterminationKind];
                }}
            >
                {([intervalDeterminationKind]) =>
                    intervalDeterminationKind === 'Other' && (
                        <form.Field
                            name={getFieldName('intervalDeterminationOther')}
                            validators={{
                                onChange: isSingleClassification
                                    ? validators.editElectricalProjectInspectionPlanCommand.intervalDeterminationOther
                                    : validators.patchElectricalProjectInspectionPlanReportCommand.intervalDeterminationOther,
                                onChangeAsync: patchField('IntervalDeterminationOther'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <ElseField>
                                    <Label label="Anders" className="w-full">
                                        <Input
                                            name={name}
                                            placeholder="Noteer inspectie interval anders"
                                            className="w-full flex flex-row"
                                            value={state.value ?? ''}
                                            onChange={e => handleChange(e.target.value)}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                </ElseField>
                            )}
                        </form.Field>
                    )}
            </form.Subscribe>

            <form.Field
                name={getFieldName('additionalInstructions')}
                validators={{ onChange: z.array(z.string()) }}
                children={({ handleChange, state, name }) => (
                    <Label label={t('information.form.additionalTopics.label')}>
                        <MultipleOptionField
                            name={name}
                            options={filteredAdditionalInstructionOptions}
                            handleChange={(newValue) => {
                                const previousValue = form.getFieldValue(getFieldName('additionalInstructions'));
                                handleChange(newValue);
                                handleSelectlistItemChange('AdditionalInstruction', previousValue, newValue);
                            }}
                            state={state}
                            render={MultipleOptionItem}
                            emptyOptionsMessage="Geen aanvullende instructies beschikbaar"
                            placeholder="Selecteer aanvullende instructies"
                            dropdown
                            disabled={isUserOffline}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            />

            <form.Field
                name={getFieldName('inspectionComponents')}
                validators={{ onChange: z.array(z.string()) }}
                children={({ handleChange, state, name }) => (
                    <Label label={t('information.form.controlPoints.label')}>
                        <MultipleOptionField
                            name={name}
                            options={filteredInspectionComponentOptions}
                            handleChange={(newValue) => {
                                const previousValue = form.getFieldValue(getFieldName('inspectionComponents'));
                                handleChange(newValue);
                                handleSelectlistItemChange('InspectionComponent', previousValue, newValue);
                            }}
                            state={state}
                            render={MultipleOptionItem}
                            emptyOptionsMessage="Geen inspectie componenten beschikbaar"
                            placeholder="Selecteer inspectie componenten"
                            dropdown
                            disabled={isUserOffline}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            />
        </div>
    );
}
